import { Uuid } from "@/domains/global/identifiers";
import { TextMessageSender } from "@/domains/integrations/text-message-senders";
import { VerificationStatus } from "@/domains/integrations/verification-status";
import { mask } from "@/modules/phone-numbers";
import { TextMessageSenderRaw } from "@/store/text-message-senders/types";
import { action, computed, makeObservable } from "mobx";
import { observable } from "mobx";

export class TextMessageSenderObservable implements TextMessageSender {
  private _id: Uuid;
  private _spaceAccountId: Uuid;
  private _verified: boolean;
  private _phoneNumber: string;
  private _verificationToken: string | undefined;
  private _verificationStatus: VerificationStatus;
  private _canDelete: boolean;

  constructor({
    id,
    phone_number,
    is_verified,
    space_account_id,
    can_delete = true,
  }: TextMessageSenderRaw) {
    this._id = id;
    this._phoneNumber = phone_number;
    this._verified = is_verified;
    this._spaceAccountId = space_account_id;
    this._canDelete = can_delete;
    this._verificationStatus = is_verified
      ? VerificationStatus.Verified
      : VerificationStatus.Unverified;

    makeObservable<
      TextMessageSenderObservable,
      | "_id"
      | "_phoneNumber"
      | "_verified"
      | "_spaceAccountId"
      | "_verificationToken"
      | "_verificationStatus"
      | "_canDelete"
    >(this, {
      _id: observable,
      id: computed,
      setId: action,
      reset: action,

      _spaceAccountId: observable,
      spaceAccountId: computed,
      setSpaceAccountId: action,

      _verified: observable,
      isVerified: computed,

      _phoneNumber: observable,
      input: computed,
      setInput: action,
      isInputValid: computed,

      _verificationStatus: observable,
      verificationStatus: computed,
      setVerificationStatus: action,

      _verificationToken: observable,
      setVerificationToken: action,
      verificationToken: computed,

      _canDelete: observable,
      canDelete: computed,
      setCanDelete: action,
    });
  }

  get id() {
    return this._id;
  }

  get spaceAccountId() {
    return this._spaceAccountId;
  }

  get isVerified() {
    return this._verified;
  }

  get input() {
    return this._phoneNumber;
  }

  get isInputValid() {
    // with masking we  can skip validation
    return true;
  }

  get verificationToken() {
    return this._verificationToken;
  }

  get verificationStatus() {
    return this._verificationStatus;
  }

  get canDelete() {
    return this._canDelete;
  }

  setId = (id: Uuid) => {
    this._id = id;
  };

  setSpaceAccountId = (spaceAccountId: Uuid) => {
    this._spaceAccountId = spaceAccountId;
  };

  setInput = (phoneNumber: string) => {
    // Handle empty input
    if (!phoneNumber) {
      this._phoneNumber = "";
      return;
    }

    // Apply masking to the extracted digits
    this._phoneNumber = mask(phoneNumber);
  };

  setVerificationToken = (verificationToken: string) => {
    this._verificationToken = verificationToken;

    if (this.verificationStatus === VerificationStatus.Invalid) {
      this.setVerificationStatus(VerificationStatus.AwaitsVerification);
    }
  };

  setVerificationStatus = (verificationStatus: VerificationStatus) => {
    this._verificationStatus = verificationStatus;

    this._verified = verificationStatus === VerificationStatus.Verified;
  };

  setCanDelete = (canDelete: boolean) => {
    this._canDelete = canDelete;
  };

  reset = (data: TextMessageSenderRaw) => {
    this._id = data.id;
    this._phoneNumber = data.phone_number;
    this._verified = data.is_verified;
    this._spaceAccountId = data.space_account_id;
  };
}
